import { isShortMapsUrl, parseMapsUrl } from './geo.js';
import { extractYouTubeId } from './youtube.js';

/**
 * סיווג מה שנכתב בעמודת "תמונה" של המשימה. המנהלת עלולה לכתוב שם לא רק תמונה אלא גם קישור למיקום במפות, כתובת ("הנשיא 5,
 * תל אביב"), סרטון או דף — ובמקום "תמונה שבורה" כל סוג מוצג כמו שצריך: מיקום וכתובת ככפתור ניווט, סרטון כנגן, תמונה
 * כתמונה, והשאר ככפתור קישור.
 */
export type MediaKind = 'maps' | 'address' | 'youtube' | 'image' | 'unknown' | 'link' | 'none';

const MAPS_LINK_RE =
  /^https?:\/\/(?:(?:www\.|[a-z]{2,3}\.)?google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps|g\.co\/(?:kgs|maps)|maps\.apple\.com|(?:www\.)?waze\.com\/(?:ul|live-map|[a-z-]+\/live-map)|ul\.waze\.com)/i;

const IMAGE_PATH_RE = /\.(?:jpe?g|png|gif|webp|avif|bmp|svg)(?:[?#].*)?$/i;
const IMAGE_HOST_RE = /^https?:\/\/(?:drive\.google\.com|docs\.google\.com|lh\d\.googleusercontent\.com|i\.imgur\.com|images\.unsplash\.com)\//i;

const isHttpUrl = (s: string): boolean => /^https?:\/\/\S+$/i.test(s);

const ADDRESS_MAX_CHARS = 150;
const ADDRESS_MAX_WORDS = 12;

/** טקסט חופשי קצר (שם מקום / כתובת) — לא פסקה. פסקה ארוכה בעמודת תמונה אינה כתובת ולא תוצג */
function looksLikeAddress(s: string): boolean {
  return s.length <= ADDRESS_MAX_CHARS && s.split(/\s+/).length <= ADDRESS_MAX_WORDS && /[\p{L}\p{N}]/u.test(s);
}

/** חיפוש כתובת / שם מקום ב-Google Maps — נפתח באפליקציית המפות בטלפון ובדפדפן במחשב */
export function addressMapsHref(text: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text.trim())}`;
}

/** קישור שאפשר לפתוח ישירות באפליקציית המפות; קואורדינטות חשופות ("32.08,34.78") הופכות לקישור Google Maps */
export function mapsHref(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  if (isHttpUrl(s)) return MAPS_LINK_RE.test(s) || isShortMapsUrl(s) || parseMapsUrl(s) !== null ? s : null;
  const point = parseMapsUrl(s);
  return point ? `https://www.google.com/maps/search/?api=1&query=${point.lat},${point.lng}` : null;
}

export function classifyMediaUrl(raw: string | null | undefined): MediaKind {
  const s = (raw ?? '').trim();
  if (!s) return 'none';
  if (mapsHref(s)) return 'maps';
  if (!isHttpUrl(s)) {
    // נתיבים מקומיים (תמונה שהועלתה לשרת) ותמונות מוטמעות הן תמונות; טקסט חופשי קצר הוא כתובת / שם מקום
    if (s.startsWith('/uploads/') || /^data:image\//i.test(s)) return 'image';
    return looksLikeAddress(s) ? 'address' : 'none';
  }
  if (extractYouTubeId(s)) return 'youtube';
  if (IMAGE_PATH_RE.test(s) || IMAGE_HOST_RE.test(s)) return 'image';
  // קישור אחר: מנסים להציג כתמונה, ואם זו לא תמונה — כפתור קישור (ראו MissionMedia)
  return 'unknown';
}

/** מנרמל קישור להשוואה (למנוע כפתור מפות כפול כשאותו קישור מופיע בכמה עמודות) */
export function normalizeLinkForCompare(raw: string): string {
  return raw.trim().replace(/\/+$/, '').toLowerCase();
}
